import { Routes } from '@angular/router';
import { adminGuard } from './core/guards/admin.guard';
import { authGuard } from './core/guards/auth.guard';
import { managerGuard } from './core/guards/manager.guard';
import { releaseHomeViewGuard } from './core/guards/release-home-view.guard';
import { AdminComponent } from './features/admin/admin.component';
import { AdminReleasesComponent } from './features/admin/admin-releases.component';
import { AdminReleaseConfigComponent } from './features/admin/admin-release-config.component';
import { ManagerReleaseHomeComponent } from './features/manager-release-home/manager-release-home.component';
import { LoginComponent } from './features/login/login.component';
import { ReleaseDetailComponent } from './features/release-detail/release-detail.component';
import { ReleaseHomeComponent } from './features/release-home/release-home.component';
import { ReleasesComponent } from './features/releases/releases.component';
import { InsightsComponent } from './features/insights/insights.component';
import { TestCaseComponent } from './features/test-case/test-case.component';
import { ReleaseResolver } from './resolvers/release/release.resolver';
import { TestCaseResolver } from './resolvers/test-case/test-case.resolver';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';

export const routes: Routes = [
  {
    path: '',
    component: LoginComponent,
  },
  {
    // Invitation deep-link. The token is captured from the URL at app bootstrap
    // (AuthService.captureInviteToken) before the MSAL redirect, so here we just
    // land the user in the normal sign-in flow.
    path: 'invite',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'releases',
    component: ReleasesComponent,
    data: { breadcrumb: 'Releases' },
    canActivate: [authGuard],
  },
  {
    path: 'insights',
    component: InsightsComponent,
    // Insights picks its own release (?releaseId) rather than resolving one, so it
    // publishes the header's release context itself (Bug 56801).
    data: { breadcrumb: 'Insights', ownsReleaseContext: true },
    canActivate: [authGuard],
  },
  {
    path: 'admin',
    component: AdminComponent,
    data: { breadcrumb: 'Admin' },
    canActivate: [adminGuard],
  },
  {
    path: 'admin/releases',
    component: AdminReleasesComponent,
    data: { breadcrumb: 'Manage releases' },
    canActivate: [adminGuard],
  },
  {
    path: 'admin/releases/:releaseId',
    component: AdminReleaseConfigComponent,
    data: { breadcrumb: 'Configure release' },
    canActivate: [adminGuard],
  },
  {
    path: 'releases/:id',
    component: ReleaseHomeComponent,
    resolve: { releaseData: ReleaseResolver },
    data: {
      breadcrumb: (params: any, data: any) => {
        return `Release: ${data.releaseData?.releaseName ?? '...'}`;
      },
    },
    // releaseHomeViewGuard sends leads and admins on to the manager view unless
    // they have picked "My view" this session (Bug 56800).
    canActivate: [authGuard, releaseHomeViewGuard],
  },
  {
    path: 'releases/:id/everyone',
    component: ManagerReleaseHomeComponent,
    resolve: { releaseData: ReleaseResolver },
    data: {
      breadcrumb: (params: any, data: any) => {
        return `Release: ${data.releaseData?.releaseName ?? '...'} (everyone)`;
      },
    },
    canActivate: [managerGuard],
  },
  {
    path: 'releases/:id/plans',
    component: ReleaseDetailComponent,
    resolve: { releaseData: ReleaseResolver },
    data: {
      breadcrumb: (params: any, data: any) => {
        return `Release: ${data.releaseData?.releaseName ?? '...'}`;
      },
    },
    canActivate: [authGuard],
  },
  {
    path: 'releases/:id/test-case/:testCaseId',
    component: TestCaseComponent,
    resolve: {
      testCaseData: TestCaseResolver,
      releaseData: ReleaseResolver, // Add this to ensure release data is available
    },
    data: {
      // The execute screen is a full-bleed focused view with its own slim top bar,
      // so it replaces (not stacks on top of) the global app header.
      hideAppHeader: true,
      breadcrumb: (params: any, data: any) => {
        const title = data.testCaseData?.testCaseName ?? '...';
        return `Test Case: ${title}`;
      },
    },
    canActivate: [authGuard],
  },
];
